/**
 * Weather File List Item Component
 *
 * Individual weather file row in the list.
 * Supports selection state indication and keyboard navigation.
 */

import { useCallback, type KeyboardEvent } from 'react';
import type { WeatherFileSearchResult } from '../../types';
import { ASHRAE_CLIMATE_ZONES } from '../../types';
import { useIsMobile } from '../../../hooks';

export interface WeatherFileListItemProps {
  file: WeatherFileSearchResult;
  onSelect: () => void;
  onPreview: () => void;
  /** Whether this item is currently selected */
  isSelected?: boolean;
  /** Keyboard event handler for list navigation */
  onKeyDown?: (e: KeyboardEvent) => void;
}

export function WeatherFileListItem({
  file,
  onSelect,
  onPreview,
  isSelected = false,
  onKeyDown,
}: WeatherFileListItemProps) {
  const isMobile = useIsMobile();
  const climateZoneInfo = file.climateZone.ashrae
    ? ASHRAE_CLIMATE_ZONES[file.climateZone.ashrae]
    : null;

  // Handle keyboard events on the item
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Handle Enter/Space for selection
      if (e.key === 'Enter') {
        e.preventDefault();
        onSelect();
      } else if (e.key === ' ') {
        e.preventDefault();
        onPreview();
      }
      // Delegate arrow keys to parent
      onKeyDown?.(e);
    },
    [onSelect, onPreview, onKeyDown]
  );

  return (
    <div
      data-weather-file-item
      tabIndex={0}
      role="option"
      aria-selected={isSelected}
      onKeyDown={handleKeyDown}
      className={`group flex items-center gap-4 rounded-lg border p-3 transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
        isSelected
          ? 'border-primary bg-primary/5 ring-1 ring-primary'
          : 'hover:bg-muted/50'
      }`}
    >
      {/* Location icon / Selected indicator */}
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
          isSelected
            ? 'bg-primary text-primary-foreground'
            : 'bg-primary/10 text-primary'
        }`}
        aria-hidden="true"
      >
        {isSelected ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
        )}
      </div>

      {/* Main content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate font-medium">{file.location.city}</h3>
          {file.popularity && file.popularity >= 85 && (
            <span className="shrink-0 text-amber-500" title="Popular location">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </span>
          )}
        </div>
        <p className="truncate text-sm text-muted-foreground">
          {file.location.region}, {file.location.country}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {/* Climate zone badge */}
          {file.climateZone.ashrae && (
            <span
              className="rounded bg-blue-100 px-1.5 py-0.5 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
              title={climateZoneInfo?.description}
            >
              {file.climateZone.ashrae}
            </span>
          )}

          {/* Source type */}
          <span className="rounded bg-gray-100 px-1.5 py-0.5 dark:bg-gray-800">
            {file.source.type}
          </span>

          {/* Year range */}
          {file.source.years && (
            <span className="rounded bg-purple-100 px-1.5 py-0.5 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
              {file.source.years}
            </span>
          )}

          {/* Elevation */}
          <span>
            {file.location.elevation}m
          </span>

          {/* Distance if available */}
          {file.distance !== undefined && (
            <span className="text-green-600 dark:text-green-400">
              {file.distance < 1
                ? `${Math.round(file.distance * 1000)}m away`
                : `${Math.round(file.distance)}km away`}
            </span>
          )}
        </div>
      </div>

      {/* Stats preview */}
      {file.stats && (
        <div className="hidden shrink-0 text-right text-xs text-muted-foreground sm:block">
          {file.stats.avgTemp !== undefined && (
            <div>
              Avg: {file.stats.avgTemp.toFixed(1)}°C
            </div>
          )}
          {file.stats.minTemp !== undefined && file.stats.maxTemp !== undefined && (
            <div>
              {file.stats.minTemp.toFixed(0)}° / {file.stats.maxTemp.toFixed(0)}°C
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPreview();
          }}
          className={`rounded-md px-2 py-1 text-sm text-muted-foreground transition-opacity hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary ${
            isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'
          }`}
          aria-label={`Preview ${file.location.city} weather file`}
          type="button"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          className={`rounded-md px-3 py-1 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
            isSelected
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-primary text-primary-foreground hover:bg-primary/90'
          }`}
          aria-label={isSelected ? `${file.location.city} is selected` : `Use ${file.location.city} weather file`}
          type="button"
        >
          {isSelected ? 'Selected' : 'Use'}
        </button>
      </div>
    </div>
  );
}
